/*
 * This file is used to define the mapping between frontend states and the ticket states on the backend side.
 * The mapping is defined for each role and also separately for receiving and transfering.
 * For example TicketRoutesFrontOffice defines the mapping for front office users on the incoming/receiving bank side:
 *     GIVEN the ticket has the status (the last fact/event happend for this ticket) is
 *     'digital.fino.cyclope.api.events.FaxDocumentReceived' aka. FAX_DOCUMENT_RECEIVED
 *     WHEN you want the waitForTransferring state view beeing displeyd for Front Office people in this case
 *     THEN add TicketStatesReceiving.FAX_DOCUMENT_RECEIVED to the states array in TicketRoutesFrontOffice.
 */
import _ from 'lodash';
import {
  TicketStatesTransferring,
  TicketStatesReceiving,
  TicketStatesGlobal,
  ViewStatus,
} from './ticket.constants.js';

export const authorization = (options = {}) => {
  return _.merge(
    {
      route: 'app.ticket.authorization',
      deadline: {
        type: 'second',
        days: 5,
      },
    },
    options,
  );
};

export const upload = (options = {}) => {
  return _.merge(
    {
      route: 'app.ticket.upload',
      deadline: {
        type: 'second',
        days: 5,
      },
    },
    options,
  );
};

export const select = (options = {}) => {
  return _.merge(
    {
      route: 'app.ticket.select',
      deadline: {
        type: 'second',
        days: 5,
      },
    },
    options,
  );
};

export const authorizationReceiving = (options = {}) => {
  return _.merge(
    {
      route: 'app.ticket.authorizationReceiving',
      deadline: {
        type: 'first',
        days: 2,
      },
    },
    options,
  );
};

export const waitForTransferring = (options = {}) => {
  return _.merge(
    {
      route: 'app.ticket.waitForTransferring',
      deadline: {
        type: 'second',
        days: 5,
      },
    },
    options,
  );
};

export const downloadBalanceList = (options = {}) => {
  return _.merge(
    {
      route: 'app.ticket.downloadBalanceList',
      deadline: {
        type: 'third',
        days: 5,
      },
    },
    options,
  );
};

export const finances = (options = {}) => {
  return _.merge(
    {
      route: 'app.ticket.finances',
      deadline: {
        type: 'third',
        days: 5,
      },
    },
    options,
  );
};

export const standingOrders = (options = {}) => {
  return _.merge(
    {
      route: 'app.ticket.standingOrders',
      deadline: {
        type: 'third',
        days: 5,
      },
    },
    options,
  );
};

export const confirm = (options = {}) => {
  return _.merge(
    {
      route: 'app.ticket.confirm',
      deadline: {
        type: 'third',
        days: 5,
      },
      readonly: {
        forcedShipment: (fs) => !!fs,
      },
    },
    options,
  );
};

export const transferringCompleted = (options = {}) => {
  return _.merge(
    {
      route: 'app.ticket.transferringCompleted',
      viewStatus: {},
      actions: [],
      hidden: true,
    },
    options,
  );
};

export const receivingCompleted = (options = {}) => {
  return _.merge(
    {
      route: 'app.ticket.receivingCompleted',
      viewStatus: {},
      hidden: true,
    },
    options,
  );
};

export const transferringClosed = (options = {}) => {
  return _.merge(
    {
      route: 'app.ticket.transferringClosed',
      viewStatus: {},
      hidden: true,
    },
    options,
  );
};

export const receivingClosed = (options = {}) => {
  return _.merge(
    {
      route: 'app.ticket.receivingClosed',
      viewStatus: {},
      hidden: true,
    },
    options,
  );
};

export const dwsReceivingStarted = (options = {}) => {
  return _.merge(
    {
      route: 'app.ticket.dwsReceivingStarted',
      deadline: {
        type: 'first',
        days: 21,
      },
    },
    options,
  );
};

export const dwsReceivingCompleted = (options = {}) => {
  return _.merge(
    {
      route: 'app.ticket.dwsReceivingCompleted',
      viewStatus: {},
      hidden: true,
    },
    options,
  );
};

export const dwsReceivingCanceled = (options = {}) => {
  return _.merge(
    {
      route: 'app.ticket.dwsReceivingCanceled',
      viewStatus: {},
      hideHeader: true,
      hidden: true,
    },
    options,
  );
};

export const TicketRoutesDWS = [
  dwsReceivingStarted({
    states: [TicketStatesReceiving.DEPOT_TRANSFER_STARTED],
  }),
  dwsReceivingCompleted({
    states: [TicketStatesReceiving.DEPOT_TRANSFER_COMPLETED],
  }),

  dwsReceivingCanceled({
    states: [TicketStatesReceiving.DEPOT_TRANSFER_CANCELED],
  }),
];

export const TicketRoutesTransferring = [
  transferringClosed({
    states: [
      TicketStatesTransferring.REJECTED_REQUEST,
      TicketStatesTransferring.REJECTION_SENT,

      // this seems weird but the ticket has only one status
      // and yields for both transferring and receiving.
      TicketStatesReceiving.CLOSED_BY_CUSTOMER,
    ],
  }),
  authorization({
    states: [
      TicketStatesTransferring.REQUEST_DATA_COMPLETED,
      TicketStatesTransferring.RECEIVED_OUTGOING_REQUEST,
    ],
  }),
  upload({
    states: [
      TicketStatesTransferring.TRANSACTION_DATA_ADDED,
      TicketStatesTransferring.ACCEPT_OUTGOING_REQUEST,
      TicketStatesTransferring.DIRECT_DEBITS_IMPORTED,
      TicketStatesTransferring.RECEIVED_PAYMENTS_IMPORTED,
      TicketStatesTransferring.STANDING_ORDERS_IMPORTED,
      TicketStatesTransferring.SCHEDULED_TRANSACTIONS_IMPORTED,
    ],
  }),
  select({
    states: [
      TicketStatesTransferring.TRANSACTION_DATA_COMPLETE,
      TicketStatesTransferring.TRANSACTION_DATA_ADDED,
      TicketStatesTransferring.FINANCES_VERIFIED,
    ],
    viewStatus: {
      [ViewStatus.Completed]: [TicketStatesTransferring.FINANCES_VERIFIED],
    },
  }),
  transferringCompleted({
    states: [
      TicketStatesTransferring.TRANSACTION_DATA_SENT,
      TicketStatesTransferring.TRANSACTION_DATA_SENT_VIA_FAX,
      TicketStatesTransferring.TRANSACTION_DATA_COMPLETE,
      TicketStatesTransferring.FINANCES_VERIFIED,
      TicketStatesGlobal.REQUEST_COMPLETED,
    ],
  }),
];

export const TicketRoutesReceiving = [
  receivingClosed({
    states: [
      TicketStatesReceiving.REJECTED_REQUEST,
      TicketStatesReceiving.CLOSED_BY_CUSTOMER,
    ],
  }),
  authorizationReceiving({
    states: [
      TicketStatesReceiving.INITIALIZATION_CONFIRMED_BY_USER,
      TicketStatesReceiving.RECEIVED_SIGNED_WARRANT_UPLOADED,
    ],
  }),
  waitForTransferring({
    states: [
      TicketStatesReceiving.SENT_REQUEST_TO_TRANSFERRING,
      TicketStatesReceiving.WARRANT_SHIPMENT_SKIPPED,
      TicketStatesReceiving.OUTGOING_SIGNED_WARRANT_RENDERED,
      TicketStatesReceiving.RECEIVED_REJECTION_DOCUMENT,
      TicketStatesReceiving.RECEIVED_REJECTION,
      TicketStatesReceiving.REMINDER_SENT,
      TicketStatesReceiving.REMINDER_SENT_VIA_FAX,
      TicketStatesReceiving.FAX_DOCUMENT_RECEIVED,
      TicketStatesReceiving.BALANCE_LIST_WITHDRAWN,
      TicketStatesGlobal.REMINDER_SENT,
    ],
  }),

  finances({
    states: [
      TicketStatesReceiving.INITIALIZATION_CONFIRMED,
      TicketStatesReceiving.FINANCES_RECEIVED_FROM_OLD_BANK,
      TicketStatesReceiving.TRANSACTION_DATA_RECEIVED,
      TicketStatesReceiving.BALANCE_LIST_DOCUMENT_RECEIVED,
      TicketStatesReceiving.TRANSACTION_DATA_APPROVED,
    ],
    viewStatus: {
      [ViewStatus.Pending]: [TicketStatesReceiving.INITIALIZATION_CONFIRMED],
    },
  }),
  standingOrders({
    states: [
      TicketStatesReceiving.INITIALIZATION_CONFIRMED,
      TicketStatesReceiving.TRANSACTION_DATA_APPROVED,
      TicketStatesReceiving.STANDING_ORDERS_PROCESSED,
      TicketStatesReceiving.VERIFIED_FINANCES,
      TicketStatesReceiving.BALANCE_LIST_DATA_RECEIVED,
      TicketStatesReceiving.PAYMENT_PARTNER_INFORMATION_STARTED,
      TicketStatesReceiving.DOCUMENT_SENDING_STARTED,
      TicketStatesReceiving.DOCUMENT_SENT,
      TicketStatesReceiving.DOCUMENT_SENT_VIA_FAX,
      TicketStatesReceiving.ALL_DOCUMENTS_SENT,
    ],
    viewStatus: {
      [ViewStatus.Pending]: [TicketStatesReceiving.INITIALIZATION_CONFIRMED],
      [ViewStatus.Completed]: [TicketStatesReceiving.STANDING_ORDERS_PROCESSED],
    },
  }),

  confirm({
    states: [
      TicketStatesReceiving.TRANSACTION_DATA_APPROVED,
      TicketStatesReceiving.STANDING_ORDERS_PROCESSED,
      TicketStatesReceiving.VERIFIED_FINANCES,
      TicketStatesReceiving.BALANCE_LIST_DATA_RECEIVED,
      TicketStatesReceiving.PAYMENT_PARTNER_INFORMATION_STARTED,
      TicketStatesReceiving.DOCUMENT_SENDING_STARTED,
      TicketStatesReceiving.DOCUMENT_SENT,
      TicketStatesReceiving.DOCUMENT_SENT_VIA_FAX,
      TicketStatesReceiving.ALL_DOCUMENTS_SENT,
    ],
    viewStatus: {
      [ViewStatus.Completed]: [
        TicketStatesReceiving.PAYMENT_PARTNER_INFORMATION_STARTED,
        TicketStatesReceiving.DOCUMENT_SENDING_STARTED,
        TicketStatesReceiving.DOCUMENT_SENT,
        TicketStatesReceiving.DOCUMENT_SENT_VIA_FAX,
        TicketStatesReceiving.ALL_DOCUMENTS_SENT,
      ],
    },
  }),
  receivingCompleted({
    states: [TicketStatesGlobal.REQUEST_COMPLETED],
  }),
];

export const TicketRoutesReceivingLight = [
  receivingClosed({
    states: [
      TicketStatesReceiving.REJECTED_REQUEST,
      TicketStatesReceiving.CLOSED_BY_CUSTOMER,
    ],
  }),
  authorizationReceiving({
    states: [
      TicketStatesReceiving.INITIALIZATION_CONFIRMED_BY_USER,
      TicketStatesReceiving.RECEIVED_SIGNED_WARRANT_UPLOADED,
    ],
  }),
  waitForTransferring({
    states: [
      TicketStatesReceiving.SENT_REQUEST_TO_TRANSFERRING,
      TicketStatesReceiving.OUTGOING_SIGNED_WARRANT_RENDERED,
      TicketStatesReceiving.RECEIVED_REJECTION_DOCUMENT,
      TicketStatesReceiving.WARRANT_SHIPMENT_SKIPPED,
      TicketStatesReceiving.RECEIVED_REJECTION,
      TicketStatesReceiving.REMINDER_SENT,
      TicketStatesReceiving.REMINDER_SENT_VIA_FAX,
      TicketStatesReceiving.BALANCE_LIST_WITHDRAWN,
      TicketStatesGlobal.REMINDER_SENT,
    ],
  }),
  downloadBalanceList({
    states: [
      TicketStatesReceiving.INITIALIZATION_CONFIRMED,
      TicketStatesReceiving.FINANCES_RECEIVED_FROM_OLD_BANK,
      TicketStatesReceiving.TRANSACTION_DATA_RECEIVED,
      TicketStatesReceiving.BALANCE_LIST_DOCUMENT_RECEIVED,
      TicketStatesReceiving.BALANCE_LIST_DATA_RECEIVED,
      TicketStatesReceiving.TRANSACTION_DATA_APPROVED,
      TicketStatesReceiving.STANDING_ORDERS_PROCESSED,
      TicketStatesReceiving.VERIFIED_FINANCES,
      TicketStatesReceiving.PAYMENT_PARTNER_INFORMATION_STARTED,
      TicketStatesReceiving.DOCUMENT_SENDING_STARTED,
      TicketStatesReceiving.DOCUMENT_SENT,
      TicketStatesReceiving.DOCUMENT_SENT_VIA_FAX,
      TicketStatesReceiving.ALL_DOCUMENTS_SENT,
    ],
    viewStatus: {
      [ViewStatus.Pending]: [TicketStatesReceiving.INITIALIZATION_CONFIRMED],
      [ViewStatus.Completed]: [TicketStatesReceiving.STANDING_ORDERS_PROCESSED],
    },
  }),
  confirm({
    states: [
      TicketStatesReceiving.INITIALIZATION_CONFIRMED,
      TicketStatesReceiving.TRANSACTION_DATA_APPROVED,
      TicketStatesReceiving.STANDING_ORDERS_PROCESSED,
      TicketStatesReceiving.VERIFIED_FINANCES,
      TicketStatesReceiving.BALANCE_LIST_DATA_RECEIVED,
      TicketStatesReceiving.DOCUMENT_SENDING_STARTED,
      TicketStatesReceiving.DOCUMENT_SENT,
      TicketStatesReceiving.DOCUMENT_SENT_VIA_FAX,
      TicketStatesReceiving.ALL_DOCUMENTS_SENT,
      TicketStatesReceiving.PAYMENT_PARTNER_ADDRESS_SAVED,
      TicketStatesReceiving.PAYMENT_PARTNER_INFORMATION_STARTED,
    ],
    viewStatus: {
      [ViewStatus.Pending]: [TicketStatesReceiving.INITIALIZATION_CONFIRMED],
      [ViewStatus.Completed]: [
        TicketStatesReceiving.PAYMENT_PARTNER_INFORMATION_STARTED,
        TicketStatesReceiving.DOCUMENT_SENDING_STARTED,
        TicketStatesReceiving.DOCUMENT_SENT,
        TicketStatesReceiving.DOCUMENT_SENT_VIA_FAX,
        TicketStatesReceiving.ALL_DOCUMENTS_SENT,
      ],
    },
  }),
  receivingCompleted({
    states: [TicketStatesGlobal.REQUEST_COMPLETED],
  }),
];

export const TicketRoutesReceivingDirect = [
  receivingClosed({
    states: [
      TicketStatesReceiving.REJECTED_REQUEST,
      TicketStatesReceiving.CLOSED_BY_CUSTOMER,
    ],
  }),
  authorizationReceiving({
    states: [
      TicketStatesReceiving.INITIALIZATION_CONFIRMED_BY_USER,
      TicketStatesReceiving.RECEIVED_SIGNED_WARRANT_UPLOADED,
    ],
  }),
  waitForTransferring({
    states: [
      TicketStatesReceiving.FAX_DOCUMENT_RECEIVED,
      TicketStatesReceiving.SENT_REQUEST_TO_TRANSFERRING,
      TicketStatesReceiving.WARRANT_SHIPMENT_SKIPPED,
      TicketStatesReceiving.OUTGOING_SIGNED_WARRANT_RENDERED,
      TicketStatesReceiving.RECEIVED_REJECTION_DOCUMENT,
      TicketStatesReceiving.RECEIVED_REJECTION,
      TicketStatesReceiving.INITIALIZATION_CONFIRMED,
      TicketStatesReceiving.REMINDER_SENT,
      TicketStatesReceiving.REMINDER_SENT_VIA_FAX,
      TicketStatesReceiving.BALANCE_LIST_WITHDRAWN,
      TicketStatesGlobal.REMINDER_SENT,
    ],
  }),
  finances({
    states: [
      TicketStatesReceiving.FINANCES_RECEIVED_FROM_OLD_BANK,
      TicketStatesReceiving.TRANSACTION_DATA_RECEIVED,
      TicketStatesReceiving.BALANCE_LIST_DOCUMENT_RECEIVED,
      TicketStatesReceiving.TRANSACTION_DATA_APPROVED,
    ],
  }),
  standingOrders({
    states: [
      TicketStatesReceiving.INITIALIZATION_CONFIRMED,
      TicketStatesReceiving.TRANSACTION_DATA_APPROVED,
      TicketStatesReceiving.VERIFIED_FINANCES,
      TicketStatesReceiving.BALANCE_LIST_DATA_RECEIVED,
      TicketStatesReceiving.DOCUMENT_SENDING_STARTED,
      TicketStatesReceiving.DOCUMENT_SENT,
      TicketStatesReceiving.DOCUMENT_SENT_VIA_FAX,
      TicketStatesReceiving.ALL_DOCUMENTS_SENT,
    ],
    viewStatus: {
      [ViewStatus.Pending]: [TicketStatesReceiving.INITIALIZATION_CONFIRMED],
      [ViewStatus.Completed]: [TicketStatesReceiving.STANDING_ORDERS_PROCESSED],
    },
  }),
  confirm({
    states: [
      TicketStatesReceiving.INITIALIZATION_CONFIRMED,
      TicketStatesReceiving.TRANSACTION_DATA_APPROVED,
      TicketStatesReceiving.STANDING_ORDERS_PROCESSED,
      TicketStatesReceiving.VERIFIED_FINANCES,
      TicketStatesReceiving.BALANCE_LIST_DATA_RECEIVED,
      TicketStatesReceiving.DOCUMENT_SENDING_STARTED,
      TicketStatesReceiving.DOCUMENT_SENT,
      TicketStatesReceiving.DOCUMENT_SENT_VIA_FAX,
      TicketStatesReceiving.ALL_DOCUMENTS_SENT,
      TicketStatesReceiving.PAYMENT_PARTNER_ADDRESS_SAVED,
      TicketStatesReceiving.PAYMENT_PARTNER_INFORMATION_STARTED,
    ],
    viewStatus: {
      [ViewStatus.Pending]: [TicketStatesReceiving.INITIALIZATION_CONFIRMED],
      [ViewStatus.Completed]: [
        TicketStatesReceiving.PAYMENT_PARTNER_INFORMATION_STARTED,
        TicketStatesReceiving.DOCUMENT_SENDING_STARTED,
        TicketStatesReceiving.DOCUMENT_SENT,
        TicketStatesReceiving.DOCUMENT_SENT_VIA_FAX,
        TicketStatesReceiving.ALL_DOCUMENTS_SENT,
      ],
    },
  }),
  receivingCompleted({
    states: [TicketStatesGlobal.REQUEST_COMPLETED],
  }),
];

export const TicketRoutesDataCapturingProvider = [
  waitForTransferring({
    states: [
      TicketStatesReceiving.INITIALIZATION_CONFIRMED,
      TicketStatesReceiving.INITIALIZATION_CONFIRMED_BY_USER,
      TicketStatesReceiving.WARRANT_SIGNED,
      TicketStatesReceiving.SENT_REQUEST_TO_TRANSFERRING,
      TicketStatesReceiving.OUTGOING_SIGNED_WARRANT_RENDERED,
      TicketStatesReceiving.RECEIVED_REJECTION_DOCUMENT,
      TicketStatesReceiving.RECEIVED_REJECTION,
      TicketStatesReceiving.REMINDER_SENT,
      TicketStatesReceiving.REMINDER_SENT_VIA_FAX,
      TicketStatesReceiving.BALANCE_LIST_WITHDRAWN,
      TicketStatesGlobal.REMINDER_SENT,
    ],
  }),
  finances({
    states: [
      TicketStatesReceiving.INITIALIZATION_CONFIRMED,
      TicketStatesReceiving.FINANCES_RECEIVED_FROM_OLD_BANK,
      TicketStatesReceiving.TRANSACTION_DATA_RECEIVED,
      TicketStatesReceiving.BALANCE_LIST_DOCUMENT_RECEIVED,
      TicketStatesReceiving.TRANSACTION_DATA_APPROVED,
    ],
    viewStatus: {
      [ViewStatus.Pending]: [TicketStatesReceiving.INITIALIZATION_CONFIRMED],
    },
  }),
  receivingCompleted({
    states: [
      TicketStatesReceiving.VERIFIED_FINANCES,
      TicketStatesReceiving.BALANCE_LIST_DATA_RECEIVED,
      TicketStatesGlobal.REQUEST_COMPLETED,
    ],
  }),
];

export const TicketRoutesCustomer = [
  receivingClosed({
    states: [
      TicketStatesReceiving.REJECTED_REQUEST,
      TicketStatesReceiving.CLOSED_BY_CUSTOMER,
    ],
  }),
  confirm({
    states: [
      TicketStatesReceiving.INITIALIZATION_CONFIRMED,
      TicketStatesReceiving.TRANSACTION_DATA_APPROVED,
      TicketStatesReceiving.STANDING_ORDERS_PROCESSED,
      TicketStatesReceiving.VERIFIED_FINANCES,
      TicketStatesReceiving.DOCUMENT_SENDING_STARTED,
      TicketStatesReceiving.DOCUMENT_SENT,
      TicketStatesReceiving.DOCUMENT_SENT_VIA_FAX,
      TicketStatesReceiving.ALL_DOCUMENTS_SENT,
      TicketStatesReceiving.SENT_REQUEST_TO_TRANSFERRING,
      TicketStatesReceiving.OUTGOING_SIGNED_WARRANT_RENDERED,
      TicketStatesReceiving.TRANSACTION_DATA_RECEIVED,
      TicketStatesReceiving.RECEIVED_SIGNED_WARRANT_UPLOADED,
      TicketStatesReceiving.REQUEST_FORM_FILLED,
      TicketStatesReceiving.WARRANT_SIGNED,
      TicketStatesReceiving.BALANCE_LIST_DOCUMENT_RECEIVED,
      TicketStatesReceiving.BALANCE_LIST_DATA_RECEIVED,
      TicketStatesReceiving.PAYMENT_PARTNER_ADDRESS_SAVED,
      TicketStatesReceiving.PAYMENT_PARTNER_INFORMATION_STARTED,
    ],
    viewStatus: {
      [ViewStatus.Pending]: [
        TicketStatesReceiving.INITIALIZATION_CONFIRMED,
        TicketStatesReceiving.SENT_REQUEST_TO_TRANSFERRING,
        TicketStatesReceiving.OUTGOING_SIGNED_WARRANT_RENDERED,
        TicketStatesReceiving.RECEIVED_SIGNED_WARRANT_UPLOADED,
        TicketStatesReceiving.TRANSACTION_DATA_APPROVED,
        TicketStatesReceiving.TRANSACTION_DATA_RECEIVED,
        TicketStatesReceiving.REQUEST_FORM_FILLED,
        TicketStatesReceiving.BALANCE_LIST_DOCUMENT_RECEIVED,
        TicketStatesReceiving.WARRANT_SIGNED,
      ],
      [ViewStatus.Completed]: [
        TicketStatesReceiving.PAYMENT_PARTNER_INFORMATION_STARTED,
        TicketStatesReceiving.DOCUMENT_SENDING_STARTED,
        TicketStatesReceiving.DOCUMENT_SENT,
        TicketStatesReceiving.DOCUMENT_SENT_VIA_FAX,
        TicketStatesReceiving.ALL_DOCUMENTS_SENT,
      ],
    },
  }),
];

export const TicketRoutesFrontOffice = [
  waitForTransferring({
    states: [
      TicketStatesReceiving.INITIALIZATION_CONFIRMED,
      TicketStatesReceiving.INITIALIZATION_CONFIRMED_BY_USER,
      TicketStatesReceiving.FAX_DOCUMENT_RECEIVED,
      TicketStatesReceiving.WARRANT_SIGNED,
      TicketStatesReceiving.SENT_REQUEST_TO_TRANSFERRING,
      TicketStatesReceiving.OUTGOING_SIGNED_WARRANT_RENDERED,
      TicketStatesReceiving.RECEIVED_REJECTION_DOCUMENT,
      TicketStatesReceiving.RECEIVED_REJECTION,
      TicketStatesReceiving.REMINDER_SENT,
      TicketStatesReceiving.REMINDER_SENT_VIA_FAX,
      TicketStatesReceiving.BALANCE_LIST_WITHDRAWN,
      TicketStatesGlobal.REMINDER_SENT,
    ],
  }),
  confirm({
    states: [
      TicketStatesReceiving.TRANSACTION_DATA_APPROVED,
      TicketStatesReceiving.STANDING_ORDERS_PROCESSED,
      TicketStatesReceiving.VERIFIED_FINANCES,
      TicketStatesReceiving.DOCUMENT_SENDING_STARTED,
      TicketStatesReceiving.DOCUMENT_SENT,
      TicketStatesReceiving.DOCUMENT_SENT_VIA_FAX,
      TicketStatesReceiving.ALL_DOCUMENTS_SENT,
      TicketStatesReceiving.TRANSACTION_DATA_RECEIVED,
      TicketStatesReceiving.RECEIVED_SIGNED_WARRANT_UPLOADED,
      TicketStatesReceiving.REQUEST_FORM_FILLED,
      TicketStatesReceiving.PAYMENT_PARTNER_ADDRESS_SAVED,
      TicketStatesReceiving.BALANCE_LIST_DOCUMENT_RECEIVED,
      TicketStatesReceiving.BALANCE_LIST_DATA_RECEIVED,
      TicketStatesReceiving.PAYMENT_PARTNER_INFORMATION_STARTED,
    ],
    viewStatus: {
      [ViewStatus.Pending]: [
        TicketStatesReceiving.RECEIVED_SIGNED_WARRANT_UPLOADED,
        TicketStatesReceiving.TRANSACTION_DATA_APPROVED,
        TicketStatesReceiving.TRANSACTION_DATA_RECEIVED,
        TicketStatesReceiving.REQUEST_FORM_FILLED,
        TicketStatesReceiving.BALANCE_LIST_DOCUMENT_RECEIVED,
        TicketStatesReceiving.WARRANT_SIGNED,
      ],
      [ViewStatus.Completed]: [
        TicketStatesReceiving.PAYMENT_PARTNER_INFORMATION_STARTED,
        TicketStatesReceiving.DOCUMENT_SENDING_STARTED,
        TicketStatesReceiving.DOCUMENT_SENT,
        TicketStatesReceiving.DOCUMENT_SENT_VIA_FAX,
        TicketStatesReceiving.ALL_DOCUMENTS_SENT,
      ],
    },
  }),
  receivingCompleted({
    states: [TicketStatesGlobal.REQUEST_COMPLETED],
  }),
];

export const TicketRoutesFrontOfficeWithStandingOrders = [
  waitForTransferring({
    states: [
      TicketStatesReceiving.INITIALIZATION_CONFIRMED,
      TicketStatesReceiving.INITIALIZATION_CONFIRMED_BY_USER,
      TicketStatesReceiving.FAX_DOCUMENT_RECEIVED,
      TicketStatesReceiving.WARRANT_SIGNED,
      TicketStatesReceiving.SENT_REQUEST_TO_TRANSFERRING,
      TicketStatesReceiving.OUTGOING_SIGNED_WARRANT_RENDERED,
      TicketStatesReceiving.RECEIVED_REJECTION_DOCUMENT,
      TicketStatesReceiving.RECEIVED_REJECTION,
      TicketStatesReceiving.REMINDER_SENT,
      TicketStatesReceiving.REMINDER_SENT_VIA_FAX,
      TicketStatesReceiving.BALANCE_LIST_WITHDRAWN,
      TicketStatesGlobal.REMINDER_SENT,
    ],
  }),
  standingOrders({
    states: [
      TicketStatesReceiving.INITIALIZATION_CONFIRMED,
      TicketStatesReceiving.TRANSACTION_DATA_APPROVED,
      TicketStatesReceiving.VERIFIED_FINANCES,
      TicketStatesReceiving.BALANCE_LIST_DATA_RECEIVED,
      TicketStatesReceiving.DOCUMENT_SENDING_STARTED,
      TicketStatesReceiving.DOCUMENT_SENT,
      TicketStatesReceiving.DOCUMENT_SENT_VIA_FAX,
      TicketStatesReceiving.ALL_DOCUMENTS_SENT,
    ],
    viewStatus: {
      [ViewStatus.Pending]: [TicketStatesReceiving.INITIALIZATION_CONFIRMED],
      [ViewStatus.Completed]: [TicketStatesReceiving.STANDING_ORDERS_PROCESSED],
    },
  }),
  confirm({
    states: [
      TicketStatesReceiving.TRANSACTION_DATA_APPROVED,
      TicketStatesReceiving.STANDING_ORDERS_PROCESSED,
      TicketStatesReceiving.VERIFIED_FINANCES,
      TicketStatesReceiving.DOCUMENT_SENDING_STARTED,
      TicketStatesReceiving.DOCUMENT_SENT,
      TicketStatesReceiving.DOCUMENT_SENT_VIA_FAX,
      TicketStatesReceiving.ALL_DOCUMENTS_SENT,
      TicketStatesReceiving.TRANSACTION_DATA_RECEIVED,
      TicketStatesReceiving.RECEIVED_SIGNED_WARRANT_UPLOADED,
      TicketStatesReceiving.REQUEST_FORM_FILLED,
      TicketStatesReceiving.PAYMENT_PARTNER_ADDRESS_SAVED,
      TicketStatesReceiving.BALANCE_LIST_DOCUMENT_RECEIVED,
      TicketStatesReceiving.BALANCE_LIST_DATA_RECEIVED,
      TicketStatesReceiving.PAYMENT_PARTNER_INFORMATION_STARTED,
    ],
    viewStatus: {
      [ViewStatus.Pending]: [
        TicketStatesReceiving.RECEIVED_SIGNED_WARRANT_UPLOADED,
        TicketStatesReceiving.TRANSACTION_DATA_APPROVED,
        TicketStatesReceiving.TRANSACTION_DATA_RECEIVED,
        TicketStatesReceiving.REQUEST_FORM_FILLED,
        TicketStatesReceiving.BALANCE_LIST_DOCUMENT_RECEIVED,
        TicketStatesReceiving.WARRANT_SIGNED,
      ],
      [ViewStatus.Completed]: [
        TicketStatesReceiving.PAYMENT_PARTNER_INFORMATION_STARTED,
        TicketStatesReceiving.DOCUMENT_SENDING_STARTED,
        TicketStatesReceiving.DOCUMENT_SENT,
        TicketStatesReceiving.DOCUMENT_SENT_VIA_FAX,
        TicketStatesReceiving.ALL_DOCUMENTS_SENT,
      ],
    },
  }),
  receivingCompleted({
    states: [TicketStatesGlobal.REQUEST_COMPLETED],
  }),
];

export const TicketRoutesTransferringFrontOffice = [
  transferringClosed({
    states: [
      TicketStatesTransferring.REJECTED_REQUEST,
      TicketStatesTransferring.REJECTION_SENT,

      // this seems weird but the ticket has only one status
      // and yields for both transferring and receiving.
      TicketStatesReceiving.CLOSED_BY_CUSTOMER,
    ],
  }),
  authorization({
    states: [
      TicketStatesTransferring.REQUEST_DATA_COMPLETED,
      TicketStatesTransferring.RECEIVED_OUTGOING_REQUEST,
    ],
    viewStatus: {
      [ViewStatus.Pending]: [
        TicketStatesTransferring.REQUEST_DATA_COMPLETED,
        TicketStatesTransferring.RECEIVED_OUTGOING_REQUEST,
      ],
    },
  }),
  upload({
    states: [
      TicketStatesTransferring.TRANSACTION_DATA_ADDED,
      TicketStatesTransferring.ACCEPT_OUTGOING_REQUEST,
      TicketStatesTransferring.DIRECT_DEBITS_IMPORTED,
      TicketStatesTransferring.RECEIVED_PAYMENTS_IMPORTED,
      TicketStatesTransferring.STANDING_ORDERS_IMPORTED,
      TicketStatesTransferring.SCHEDULED_TRANSACTIONS_IMPORTED,
    ],
    viewStatus: {
      [ViewStatus.Pending]: [
        TicketStatesTransferring.TRANSACTION_DATA_ADDED,
        TicketStatesTransferring.ACCEPT_OUTGOING_REQUEST,
        TicketStatesTransferring.DIRECT_DEBITS_IMPORTED,
        TicketStatesTransferring.RECEIVED_PAYMENTS_IMPORTED,
        TicketStatesTransferring.STANDING_ORDERS_IMPORTED,
        TicketStatesTransferring.SCHEDULED_TRANSACTIONS_IMPORTED,
      ],
    },
  }),
  select({
    states: [
      TicketStatesTransferring.TRANSACTION_DATA_COMPLETE,
      TicketStatesTransferring.TRANSACTION_DATA_ADDED,
      TicketStatesTransferring.FINANCES_VERIFIED,
    ],
    viewStatus: {
      [ViewStatus.Pending]: [
        TicketStatesTransferring.TRANSACTION_DATA_COMPLETE,
        TicketStatesTransferring.TRANSACTION_DATA_ADDED,
        TicketStatesTransferring.FINANCES_VERIFIED,
      ],
    },
  }),
  transferringCompleted({
    states: [
      TicketStatesTransferring.TRANSACTION_DATA_SENT,
      TicketStatesTransferring.TRANSACTION_DATA_SENT_VIA_FAX,
      TicketStatesGlobal.REQUEST_COMPLETED,
    ],
  }),
];
